import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import supabase from "../config/supabaseClient";
import "../index2.css";

// All supported formations
const formations = {
  "4-3-3": [4, 3, 3],
  "4-4-2": [4, 4, 2],
  "3-5-2": [3, 5, 2],
  "3-4-3": [3, 4, 3],
  "4-2-3-1": [4, 2, 3, 1],
  "5-3-2": [5, 3, 2],
  "5-4-1": [5, 4, 1],
  "4-5-1": [4, 5, 1],
  "3-6-1": [3, 6, 1],
  "4-1-4-1": [4, 1, 4, 1],
  "4-1-3-2": [4, 1, 3, 2],
  "4-2-2-2": [4, 2, 2, 2],
  "3-3-4": [3, 3, 4],
  "4-3-2-1": [4, 3, 2, 1],
  "3-2-3-2": [3, 2, 3, 2],
  "3-2-2-3": [3, 2, 2, 3],
  "2-3-5": [2, 3, 5],
  "WM (3-2-2-3)": [3, 2, 2, 3],
  "4-2-4": [4, 2, 4],
  "3-4-1-2": [3, 4, 1, 2],
};

const InsertAdversaryInfoPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const gameId = new URLSearchParams(location.search).get("gameId");

  const [formation, setFormation] = useState("");
  const [positions, setPositions] = useState([]);
  const [benchPlayers, setBenchPlayers] = useState([]);
  const [playerNumbers, setPlayerNumbers] = useState({});
  const [fetchError, setFetchError] = useState(null);
  const [saving, setSaving] = useState(false);

  // --- Load existing data ---
  useEffect(() => {
    const fetchExisting = async () => {
      try {
        const { data, error } = await supabase
          .from("adv_inf")
          .select("formation_position, player_number")
          .eq("game_id", gameId);

        if (error) throw error;

        if (data && data.length > 0) {
          const existingNumbers = {};
          const bench = [];
          let detectedFormation = "";

          data.forEach((row) => {
            existingNumbers[row.formation_position] = row.player_number;
            if ((row.formation_position || "").startsWith("BENCH")) {
              bench.push(row.formation_position);
            } else if (!detectedFormation && row.formation_position.includes("-L")) {
              detectedFormation = row.formation_position.split("-L")[0]; // extract "4-3-3"
            }
          });

          setPlayerNumbers(existingNumbers);
          setBenchPlayers(bench.length > 0 ? bench : []);
          setFormation(detectedFormation);
          if (detectedFormation) {
            const levels = formations[detectedFormation];
            const newPositions = ["GK"];
            levels.forEach((numPlayers, levelIndex) => {
              for (let p = 1; p <= numPlayers; p++) {
                newPositions.push(`${detectedFormation}-L${levelIndex + 1}-P${p}`);
              }
            });
            setPositions(newPositions);
          }
        } else {
          // no data found
          setBenchPlayers(Array.from({ length: 8 }, (_, i) => `BENCH-${i + 1}`));
        }
      } catch (error) {
        setFetchError("Could not fetch adversary info: " + (error.message || "Unknown error"));
      }
    };

    fetchExisting();
  }, [gameId]);

  // --- Change formation ---
  const handleFormationSelect = (e) => {
    const value = e.target.value;
    setFormation(value);

    if (!value) {
      setPositions([]);
      return;
    }

    const levels = formations[value];
    const newPositions = ["GK"];
    levels.forEach((numPlayers, levelIndex) => {
      for (let p = 1; p <= numPlayers; p++) {
        newPositions.push(`${value}-L${levelIndex + 1}-P${p}`);
      }
    });

    const newBench =
      benchPlayers && benchPlayers.length > 0
        ? benchPlayers
        : Array.from({ length: 8 }, (_, i) => `BENCH-${i + 1}`);

    setPositions(newPositions);
    setBenchPlayers(newBench);
  };

  // --- Change player number ---
  const handleNumberChange = (position, value) => {
    setPlayerNumbers((prev) => ({ ...prev, [position]: value }));
  };

  const getAllPositions = () => [...positions, ...benchPlayers];

  // --- Save all data ---
  const handleSubmit = async () => {
    setSaving(true);
    setFetchError(null);
    try {
      const allPositions = getAllPositions();

      // 1) Fetch existing rows
      const { data: existingRows, error: fetchErrorRows } = await supabase
        .from("adv_inf")
        .select("id, formation_position")
        .eq("game_id", gameId)
        .in("formation_position", allPositions);

      if (fetchErrorRows) throw fetchErrorRows;

      const existingMap = (existingRows || []).reduce((acc, row) => {
        acc[row.formation_position] = row.id;
        return acc;
      }, {});

      const toInsert = [];
      const toUpdate = [];

      for (const pos of allPositions) {
        const rawVal = playerNumbers[pos];
        const number = rawVal === undefined || rawVal === "" ? null : parseInt(rawVal, 10);

        if (existingMap[pos]) {
          toUpdate.push({ id: existingMap[pos], player_number: number });
        } else {
          toInsert.push({ game_id: gameId, formation_position: pos, player_number: number });
        }
      }

      // Insert new
      if (toInsert.length > 0) {
        const { error: insertError } = await supabase.from("adv_inf").insert(toInsert);
        if (insertError) throw insertError;
      }

      // Update existing
      if (toUpdate.length > 0) {
        const updatePromises = toUpdate.map((u) =>
          supabase.from("adv_inf").update({ player_number: u.player_number }).eq("id", u.id)
        );
        const results = await Promise.all(updatePromises);
        for (const r of results) if (r.error) throw r.error;
      }

      setSaving(false);
      navigate(-1);
    } catch (err) {
      console.error(err);
      setSaving(false);
      setFetchError("Error saving adversary info: " + (err.message || "Unknown error"));
    }
  };

  // --- Coordinate generator ---
  const getPositionCoordinates = (formation, positions) => {
    const coords = {};
    const fieldHeight = 80;
    coords["GK"] = { top: "90%", left: "50%" };

    positions.slice(1).forEach((pos) => {
      const match = pos.match(/-L(\d+)-P(\d+)/);
      const levelNum = match ? parseInt(match[1], 10) : 0;
      const playerNum = match ? parseInt(match[2], 10) : 0;

      const levels = formations[formation];
      const numPlayersInLine = levels[levelNum - 1] || 1;
      const verticalSpacing = fieldHeight / (levels.length + 1);
      const top = 90 - levelNum * verticalSpacing;
      const horizontalSpacing = 100 / (numPlayersInLine + 1);
      const left = playerNum * horizontalSpacing;

      coords[pos] = { top: `${top}%`, left: `${left}%` };
    });

    return coords;
  };

  const positionCoordinates =
    formation && positions.length > 0 ? getPositionCoordinates(formation, positions) : {};

  return (
    <div className="page insert-adversary-info">
      <button onClick={() => navigate(-1)} className="back-button">
        Back
      </button>
      {fetchError && <p className="error">{fetchError}</p>}

      <h2>{formation ? "Edit Adversary Info" : "Insert Adversary Info"}</h2>

      <div className="formation-select">
        <label>
          Formation:
          <select value={formation} onChange={handleFormationSelect}>
            <option value="">Select Formation</option>
            {Object.keys(formations).map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </label>
      </div>

      {formation && (
        <>
          <div className="mini-field-container">
            <h3>Adversary Formation: {formation}</h3>
            <div
              className="mini-field"
              style={{
                position: "relative",
                width: "400px",
                height: "600px",
                background: "darkred",
                border: "2px solid black",
                marginTop: "20px",
              }}
            >
              {positions.map((pos) => {
                const coords = positionCoordinates[pos];
                return (
                  <div
                    key={pos}
                    style={{
                      position: "absolute",
                      top: coords?.top || "50%",
                      left: coords?.left || "50%",
                      transform: "translate(-50%, -50%)",
                      backgroundColor: "white",
                      borderRadius: "50%",
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <input
                      type="number"
                      placeholder="#"
                      value={playerNumbers[pos] ?? ""}
                      onChange={(e) => handleNumberChange(pos, e.target.value)}
                      style={{
                        width: "100%",
                        height: "100%",
                        textAlign: "center",
                        border: "none",
                        outline: "none",
                        background: "transparent",
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bench Section */}
          <div className="bench-section" style={{ marginTop: "30px" }}>
            <h3>Bench Players</h3>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "10px",
                background: "#660000",
                padding: "15px",
                borderRadius: "10px",
                width: "400px",
              }}
            >
              {benchPlayers.map((benchPos) => (
                <div
                  key={benchPos}
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "white",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <input
                    type="number"
                    placeholder="#"
                    value={playerNumbers[benchPos] ?? ""}
                    onChange={(e) => handleNumberChange(benchPos, e.target.value)}
                    style={{
                      width: "100%",
                      height: "100%",
                      textAlign: "center",
                      border: "none",
                      outline: "none",
                      background: "transparent",
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={saving}
            className="submit-button"
            style={{ marginTop: "25px" }}
          >
            {saving ? "Saving..." : "Save Adversary Info"}
          </button>
        </>
      )}
    </div>
  );
};

export default InsertAdversaryInfoPage;
